/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomSelectV3.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import InfoButton from "../../../infoButton/InfoButton";

function CustomSelectV3({
  size,
  label,
  variant,
  required,
  disabled,
  optionsList = [],
  selectedValue,
  sltFldStyle,
  handleOnChange,
  placeholder = "Select an option",
  infoname,
}) {
  const [value, setValue] = React.useState(selectedValue || "");
  const selectRef = useRef(null);

  useEffect(() => {
    setValue(selectedValue || "");
  }, [selectedValue]);

  return (
    <FormControl
      variant={variant || "outlined"}
      fullWidth
      size={size}
      required={required}
      sx={{
        ...sltFldStyle,
        "& .MuiInputLabel-root": {
          position: "relative",
          transform: "none",
          marginBottom: "4px",
          fontSize: "0.85rem",
          fontWeight: 500,
          color: "rgba(0, 0, 0, 0.6)",
          "&.Mui-error": { color: "#e53935" },
          "&.Mui-disabled": { color: "#bdbdbd" },
          "& .MuiFormLabel-asterisk": { color: "red" },
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "0px",
          backgroundColor: "#fff",
          "& fieldset": { borderColor: "#d9d9d9" },
          "&:hover fieldset": { borderColor: "#8c8c8c" },
          "&.Mui-focused fieldset": { borderColor: "#000000" },
          "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
          "&.Mui-error fieldset": { borderColor: "#e53935" },
          "& .MuiSelect-select": {
            fontSize: "0.9rem",
          },
        },
      }}
    >
      <Box sx={{ display: "flex" }}>
        <InputLabel
          shrink
          htmlFor="select-field"
          style={{
            mb: 0.5,
            color: "#6f7174",
            fontSize: "0.9rem",
            fontWeight: 500,
            display: "flex",
          }}
        >
          {label}
        </InputLabel>
        <Box sx={{ width: 5, height: 5 }}>
          {infoname?.length > 0 ? (
            <InfoButton
              dimension={{ width: 15, height: 15 }}
              infoKey={infoname}
              color={"black"}
            />
          ) : null}
        </Box>
      </Box>

      <Select
        ref={selectRef}
        id="select-field"
        variant="outlined"
        disabled={disabled}
        value={value}
        onChange={handleOnChange}
        displayEmpty
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 220,
              width: selectRef.current?.offsetWidth || "auto",
              overflowY: "scroll",
              scrollbarWidth: "thin",
              scrollbarColor: "#9e9e9e #f0f0f0",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f0f0f0",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#9e9e9e",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#757575",
              },
              mt: 1,
            },
          },
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          getContentAnchorEl: null,
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography sx={{ color: "#9e9e9e", textAlign: "start" }}>
                {placeholder}
              </Typography>
            );
          }
          const selectedOption = optionsList.find(
            (opt) => opt.value === selected,
          );
          return (
            <Typography sx={{ color: "#000000", textAlign: "start" }}>
              {selectedOption?.label || ""}
            </Typography>
          );
        }}
        sx={{
          backgroundColor: "#fff",
          "& .MuiSelect-select": {
            py: "10px",
            fontSize: "0.9rem",
          },
        }}
      >
        {optionsList?.map((option) => (
          <MenuItem
            key={option?.value}
            value={option?.value}
            sx={{
              ...(value === option?.value && {
                backgroundColor: "#dce1dcff !important", // soft yellow
                fontWeight: 600,
              }),
              "&.Mui-selected:hover": {
                backgroundColor: "#d9d9d9ff !important", // hover yellow
              },
            }}
          >
            <Box sx={{ display: "flex" }}>
              {label === "Select Legal Standards" && option?.version && (
                <Typography
                  variant="body2"
                  sx={{ ml: 2, color: "text.secondary", fontStyle: "italic" }}
                >
                  {option.version}
                </Typography>
              )}
            </Box>

            {label === "Vehicle Class" ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <img
                      src={option?.image}
                      alt=""
                      style={{
                        width: 32,
                        height: 32,
                        marginRight: "8px",
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option?.name || "Unnamed"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option?.class && option?.sub_class
                        ? `${option.class} — ${option.sub_class}`
                        : "No details"}
                    </Typography>
                  </Box>
                </Box>
            ) : (
              <Box flex={1}>{option?.label}</Box>
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

CustomSelectV3.propTypes = {
  size: PropTypes.string,
  label: PropTypes.node,
  variant: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  optionsList: PropTypes.array,
  selectedValue: PropTypes.any,
  sltFldStyle: PropTypes.object,
  handleOnChange: PropTypes.func,
  placeholder: PropTypes.string,
  infoname: PropTypes.string,
};

CustomSelectV3.defaultProps = {
  size: undefined,
  label: null,
  variant: "outlined",
  required: false,
  disabled: false,
  optionsList: [],
  selectedValue: "",
  sltFldStyle: {},
  handleOnChange: () => {},
  placeholder: "Select an option",
  infoname: null,
};

export default CustomSelectV3;
